import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";
import { useSelector } from "react-redux";
import CircularProgresss from "components/CircularProgress/CircularProgresss";
import ToastModal from "views/components/ToastModal";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    backgroundColor: "#f5f8fc",
    padding: "24px",
    boxSizing: "border-box",

    [theme.breakpoints.down("sm")]: {
      padding: "12px",
    },
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",

    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
    },
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
  },

  goldIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "#fff7df",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "30px",
    marginRight: "18px",
  },

  title: {
    fontSize: "30px",
    fontWeight: 700,
    color: "#17243a",

    [theme.breakpoints.down("sm")]: {
      fontSize: "22px",
    },
  },

  subtitle: {
    color: "#64748b",
    fontSize: "14px",
    marginTop: "5px",
  },

  resetButton: {
    border: "1px solid #77a9d7",
    borderRadius: "8px",
    color: "#1976d2",
    textTransform: "none",
    fontWeight: 600,
    padding: "9px 18px",
  },

  card: {
    backgroundColor: "#fff",
    border: "1px solid #e1e7ef",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "18px",
  },

  sectionHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },

  sectionIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    backgroundColor: "#edf5ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "21px",
    marginRight: "13px",
  },

  sectionTitle: {
    fontSize: "19px",
    fontWeight: 700,
    color: "#183153",
  },

  sectionSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    marginTop: "3px",
  },

  addButton: {
    backgroundColor: "#087443",
    color: "#fff",
    borderRadius: "8px",
    padding: "10px 18px",
    textTransform: "none",
    fontWeight: 600,

    "&:hover": {
      backgroundColor: "#07633a",
    },
  },

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #dfe5ec",
    borderRadius: "8px",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "950px",

    "& th": {
      backgroundColor: "#f4f7fb",
      color: "#334155",
      fontSize: "13px",
      fontWeight: 700,
      textAlign: "left",
      padding: "12px 10px",
      borderBottom: "1px solid #dfe5ec",
      whiteSpace: "nowrap",
    },

    "& td": {
      padding: "9px 10px",
      borderBottom: "1px solid #e5e7eb",
      verticalAlign: "middle",
    },

    "& tr:last-child td": {
      borderBottom: "none",
    },
  },

  tableInput: {
    width: "100%",

    "& .MuiInputBase-root": {
      height: "44px",
      borderRadius: "7px",
      backgroundColor: "#fff",
    },

    "& input": {
      padding: "10px 12px",
      fontSize: "14px",
    },
  },

  disabledInput: {
    "& .MuiInputBase-root": {
      backgroundColor: "#f0f3f6",
    },
  },

  puritySelect: {
    width: "100%",
    height: "44px",
    border: "1px solid #d5dce5",
    borderRadius: "7px",
    paddingLeft: "10px",
    fontSize: "14px",
    backgroundColor: "#fff",
  },

  deleteButton: {
    color: "#d32f2f",
    border: "1px solid #efcaca",
    borderRadius: "7px",
    width: "40px",
    height: "40px",
    padding: "0",
    backgroundColor: "#fff7f7",

    "&:hover": {
      backgroundColor: "#fff0f0",
    },
  },

  totalBar: {
    marginTop: "18px",
    background: "#edfff5",
    border: "1px solid #d3f0df",
    borderRadius: "10px",
    padding: "16px",
  },

  totalItem: {
    textAlign: "center",
    borderRight: "1px solid #cfe7d9",

    "&:last-child": {
      borderRight: "none",
    },

    [theme.breakpoints.down("sm")]: {
      borderRight: "none",
      marginBottom: "12px",
    },
  },

  totalLabel: {
    color: "#64748b",
    fontSize: "13px",
    marginBottom: "5px",
  },

  totalValue: {
    color: "#087443",
    fontSize: "21px",
    fontWeight: 700,
  },

  parameterCard: {
    backgroundColor: "#fff",
    border: "1px solid #e1e7ef",
    borderRadius: "12px",
    padding: "22px",
    height: "100%",
    boxSizing: "border-box",
  },

  resultCard: {
    backgroundColor: "#fff",
    border: "1px solid #e1e7ef",
    borderRadius: "12px",
    padding: "22px",
    height: "100%",
    boxSizing: "border-box",
  },

  parameterLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#334155",
    marginBottom: "8px",
  },

  resultBox: {
    marginTop: "15px",
    background: "#edfff5",
    border: "1px solid #ccebd9",
    borderRadius: "10px",
    padding: "18px",
    textAlign: "center",
  },

  resultTitle: {
    color: "#19734a",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "7px",
  },

  resultValue: {
    color: "#087443",
    fontSize: "34px",
    fontWeight: 700,

    [theme.breakpoints.down("sm")]: {
      fontSize: "27px",
    },
  },

  resultDetails: {
    display: "flex",
    marginTop: "18px",
    paddingTop: "15px",
    borderTop: "1px solid #cfe9da",
  },

  resultDetail: {
    flex: 1,
    textAlign: "center",
    borderRight: "1px solid #cfe9da",

    "&:last-child": {
      borderRight: "none",
    },
  },

  resultDetailLabel: {
    color: "#64748b",
    fontSize: "12px",
    marginBottom: "5px",
  },

  resultDetailValue: {
    color: "#1e293b",
    fontSize: "16px",
    fontWeight: 700,
  },

  footer: {
    marginTop: "18px",
  },

  note: {
    padding: "13px 16px",
    borderRadius: "8px",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
  },

  calculateButton: {
    height: "52px",
    width: "100%",
    borderRadius: "8px",
    backgroundColor: "#1976d2",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "15px",

    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
}));

const CriffCalculator = ({ featureFlags }) => {
  const classes = useStyles();
  const financeCodeListFlag = featureFlags?.financeCodeListFlag
    ? JSON.parse(featureFlags?.financeCodeListFlag)
    : [];
  const [ornaments, setOrnaments] = useState([
    {
      id: Date.now(),
      name: "",
      purity: "22k",
      grossWeight: "",
      netWeight: "",
      netWeight22K: "",
    },
  ]);
  const [scheme, setScheme] = useState("non-bullet");
  const [interestType, setInterestType] = useState("flat");
  const [interestRate, setInterestRate] = useState("27");
  const [ltv, setLtv] = useState("85");
  const { authToken } = useSelector((state) => state.login.authData);
  const [eligibility, setEligibility] = useState(0);
  const [callInProgress, setCallInProgress] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastMessageType, setToastMessageType] = useState('');
  const [futureInterest, setFutureInterest] = useState("");
  const [tenure, setTenure]=useState(12);
  const [collateralValue, setCollateralValue]=useState("")
  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };
  const addOrnament = () => {
    setOrnaments((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        name: "",
        purity: "22k",
        grossWeight: "",
        netWeight: "",
        netWeight22K: "",
      },
    ]);
  };
  const calculate22KWeight = async (id, netWeight, purity) => {
    if (!netWeight || !purity) {
      updateOrnament(id, "netWeight22K", "");
      return;
    }

    try {
      const response = await apiHandler({
        url: endpoint.CALCULATE_WEIGHT,
        method: "POST",
        authToken: authToken,
        data: {
          netWeight: Number(netWeight),
          netWeightGoldPurity: purity.toLowerCase(),
          expectedNetWeightGoldPurity: "22k",
        },
      });

      if (response?.data && !response?.data?.error_code) {
        updateOrnament(id, "netWeight22K", response.data.netWeight);
      }
    } catch (error) {
      console.error("Error calculating 22K weight:", error);

      updateOrnament(id, "netWeight22K", "");
    }
  };
  const deleteOrnament = (id) => {
    setOrnaments((prev) => prev.filter((item) => item.id !== id));
  };
  const updateOrnament = (id, field, value) => {
    setOrnaments((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };
  const handleNumberChange = (id, field, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      updateOrnament(id, field, value);
    }
  };
  const get22KWeight = (ornament) => {
    const netWeight = Number(ornament.netWeight) || 0;
    const purity = Number(ornament.purity) || 22;

    return (netWeight * purity) / 22;
  };
  const totals = useMemo(() => {
    return ornaments.reduce(
      (total, ornament) => {
        total.grossWeight += Number(ornament.grossWeight) || 0;

        total.netWeight += Number(ornament.netWeight) || 0;

        total.weight22K += Number(ornament.netWeight22K) || 0;

        return total;
      },
      {
        grossWeight: 0,
        netWeight: 0,
        weight22K: 0,
      }
    );
  }, [ornaments]);
  const calculateEligibility = async () => {
  try {
    setCallInProgress(true);

    const ornamentDetails = ornaments.map((ornament) => ({
      particulars: ornament.name,
      itemCount: Number(ornament.itemCount) || 1,
      finnesseCode: ornament.purity,
      grossWeight: Number(ornament.netWeight) || 0,
      stoneWeight: 0,
      deduction: Number(ornament.deduction) || 0,
      packetNumber: ornament.packetNumber || "",
      sourceOfOwnership: ornament.sourceOfOwnership || "Self purchased",
    }));

    const response = await apiHandler({
      url: endpoint.ADMIN_CALCULATE_ELIGIBLE_AMOUNT,
      method: "POST",
      authToken: authToken,
      data: {
        ornamentDetails,
      },
    });

    console.log("Eligible Loan API Response:", response);

    if (response?.data && !response?.data?.error_code) {
      const loanAmount = Math.floor(Number(response.data.eligibleLoanAmt)) || 0;
      setCollateralValue(loanAmount)
      // First slab calculation
      let eligibleAmount = 0;

      if (loanAmount <= 294118) {
        eligibleAmount = loanAmount * 0.85;
           setLtv(85);
      } else if (loanAmount <= 625000) {
        eligibleAmount = loanAmount * 0.80;
           setLtv(80);
      } else {
        eligibleAmount = loanAmount * 0.75;
           setLtv(75);
      }
      const checkTenureOnBullet = 1 + 0.27 * tenure / 12
      const roundEligibleAmount = Math.round(eligibleAmount)
      const tenureEligibleAmount = Number(roundEligibleAmount) / checkTenureOnBullet
      const futureInterestOnBullet = roundEligibleAmount - Math.round(tenureEligibleAmount)
      const finalEligibleAmount = 
        scheme === "bullet"
          ?  Math.round(tenureEligibleAmount)
          :  roundEligibleAmount;

      setFutureInterest(futureInterestOnBullet)

      // Recalculate eligibility based on slabCheckValue
      // let finalEligibleAmount = 0;

      // if (slabCheckValue <= 250000) {
      //   finalEligibleAmount = loanAmount * 0.85;
      //   setLtv(85);
      // } else if (slabCheckValue <= 500000) {
      //   finalEligibleAmount = loanAmount * 0.80;
      //   setLtv(80);
      // } else {
      //   finalEligibleAmount = loanAmount * 0.75;
      //   setLtv(75);
      // }

      // if(scheme === "bullet"){
      //   let curretnFutureInterest = Math.round(asPerTenureInterest);
      //   setFutureInterest(curretnFutureInterest);
      // }
      setEligibility(Math.round(finalEligibleAmount));
    }
  } catch (error) {
    console.error("Error calculating eligible loan amount:", error);
  } finally {
    setCallInProgress(false);
  }
};
  const resetCalculator = () => {
    setOrnaments([
      {
        id: Date.now(),
        name: "",
        purity: 22,
        grossWeight: "",
        netWeight: "",
      },
    ]);

    setScheme("bullet");
    setInterestType("flat");
    setInterestRate("27");
    setEligibility(0);
  };
  const formatCurrency = (value) => {
    return `₹ ${Number(value || 0).toLocaleString("en-IN")}`;
  };

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.goldIcon}>💰</div>

          <div>
            <div className={classes.title}>Gold Loan Calculator</div>

            <div className={classes.subtitle}>
              Add multiple ornaments to calculate your maximum loan eligibility
            </div>
          </div>
        </div>

        <Button
          variant="outlined"
          className={classes.resetButton}
          onClick={resetCalculator}
        >
          ↻ &nbsp; Reset
        </Button>
      </div>
      <div className={classes.card}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionHeaderLeft}>
            <div className={classes.sectionIcon}>▦</div>

            <div>
              <div className={classes.sectionTitle}>Ornament Details</div>

              <div className={classes.sectionSubtitle}>
                Enter the details of each ornament
              </div>
            </div>
          </div>

          <Button
            variant="contained"
            className={classes.addButton}
            onClick={addOrnament}
          >
            + &nbsp; Add Ornament
          </Button>
        </div>
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th style={{ width: "140px" }}>Purity</th>
                {/* <th style={{ width: "170px" }}>Gross Weight (g)</th> */}
                <th style={{ width: "170px" }}>Weight (g)</th>
                <th style={{ width: "190px" }}>Weight 22K (g)</th>
                <th style={{ width: "80px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {ornaments.map((ornament, index) => (
                <tr key={ornament.id}>
                  <td>{index + 1}</td>
                  {/* <td>
                    <FormControl variant="outlined" fullWidth size="small">
                      <Select
                        value={ornament.name}
                        onChange={(event) =>
                          updateOrnament(
                            ornament.id,
                            "name",
                            event.target.value
                          )
                        }
                        className={classes.tableInput}
                      >
                        {ornamentList?.map((item) => {
                          return (
                            <MenuItem key={item.name} value={item.name}>
                              {item.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </td> */}
                  <td>
                    <FormControl variant="outlined" fullWidth size="small">
                      <Select
                        value={ornament.purity}
                        onChange={(event) => {
                          const purity = event.target.value;

                          updateOrnament(ornament.id, "purity", purity);

                          calculate22KWeight(
                            ornament.id,
                            ornament.netWeight,
                            purity
                          );
                        }}
                      >
                        {financeCodeListFlag?.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </td>
                  {/* <td>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.tableInput}
                      placeholder="0.00"
                      value={ornament.grossWeight}
                      onChange={(event) =>
                        handleNumberChange(
                          ornament.id,
                          "grossWeight",
                          event.target.value
                        )
                      }
                    />
                  </td> */}
                  <td>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={ornament.netWeight}
                      onChange={(event) => {
                        const value = event.target.value;

                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          updateOrnament(ornament.id, "netWeight", value);

                          calculate22KWeight(
                            ornament.id,
                            value,
                            ornament.purity
                          );
                        }
                      }}
                    />
                  </td>
                  <td>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled
                      value={
                        ornament.netWeight22K !== ""
                          ? Number(ornament.netWeight22K).toFixed(2)
                          : ""
                      }
                    />
                  </td>
                  <td>
                    <IconButton
                      className={classes.deleteButton}
                      onClick={() => deleteOrnament(ornament.id)}
                      disabled={ornaments.length === 1}
                    >
                      🗑
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={classes.totalBar}>
          <Grid container alignItems="center">
            <Grid item xs={12} sm={4} className={classes.totalItem}>
              <div className={classes.totalLabel}>Total</div>

              <div className={classes.totalValue}>
                {ornaments.length} Ornaments
              </div>
            </Grid>
            {/* <Grid item xs={12} sm={3} className={classes.totalItem}>
              <div className={classes.totalLabel}>Gross Weight</div>

              <div className={classes.totalValue}>
                {totals.grossWeight.toFixed(2)} g
              </div>
            </Grid> */}
            <Grid item xs={12} sm={4} className={classes.totalItem}>
              <div className={classes.totalLabel}>Net Weight</div>

              <div className={classes.totalValue}>
                {totals.netWeight.toFixed(2)} g
              </div>
            </Grid>
            <Grid item xs={12} sm={4} className={classes.totalItem}>
              <div className={classes.totalLabel}>22K Equivalent Weight</div>

              <div className={classes.totalValue}>
                {totals.weight22K.toFixed(2)} g
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <div className={classes.parameterCard}>
            <div className={classes.sectionHeader}>
              <div className={classes.sectionHeaderLeft}>
                <div className={classes.sectionIcon}>⚙</div>

                <div>
                  <div className={classes.sectionTitle}>Loan Parameters</div>

                  <div className={classes.sectionSubtitle}>
                    Select loan scheme and interest details
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.parameterLabel}>Scheme</div>

            <RadioGroup
              row
              value={scheme}
              onChange={(event) => {
                setEligibility(0)
                setFutureInterest(0)
                setScheme(event.target.value)
              }}
            >
               <FormControlLabel
                value="non-bullet"
                control={<Radio color="primary" />}
                label="Non-Bulleted"
              />

              <FormControlLabel
                value="bullet"
                control={<Radio color="primary" />}
                label="Bulleted"
              />
            </RadioGroup>
            {scheme === "bullet" &&
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <div className={classes.parameterLabel}>Interest Type</div>

                  <FormControl variant="outlined" fullWidth size="small">
                    <Select
                      value={interestType}
                      onChange={(event) => setInterestType(event.target.value)}
                      disabled
                    >
                      <MenuItem value="flat">Flat</MenuItem>

                      <MenuItem value="reducing">Reducing</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <div className={classes.parameterLabel}>
                    Interest Rate (% p.a.)
                  </div>

                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="number"
                    value={interestRate}
                    onChange={(event) => setInterestRate(event.target.value)}
                    InputProps={{
                      endAdornment: "%",
                    }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <div className={classes.parameterLabel}>
                    Tenure In Months
                  </div>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="number"
                    value={tenure}
                    onChange={(event) => setTenure(event.target.value)}
                  />
                </Grid>
              </Grid>
            }
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <div className={classes.resultCard}>
            <div className={classes.sectionHeader}>
              <div className={classes.sectionHeaderLeft}>
                <div>
                  <div className={classes.sectionTitle}>
                    Max Loan Eligibility
                  </div>

                  <div className={classes.sectionSubtitle}>
                    Based on 22K equivalent weight and selected scheme
                  </div>
                </div>
              </div>
            </div>

            <div className={classes.resultBox}>
              <div className={classes.resultTitle}>
                Maximum Loan Eligibility Amount
              </div>

              <div className={classes.resultValue}>
                {formatCurrency(eligibility)}
              </div>

              <div className={classes.resultDetails}>
                <div className={classes.resultDetail}>
                  <div className={classes.resultDetailLabel}>
                    22K Equivalent Weight
                  </div>

                  <div className={classes.resultDetailValue}>
                    {totals.weight22K.toFixed(2)} g
                  </div>
                </div>

                <div className={classes.resultDetail}>
                  <div className={classes.resultDetailLabel}>LTV</div>

                  <div className={classes.resultDetailValue}>
                    {ltv}%
                  </div>
                </div>
                <div className={classes.resultDetail}>
                  <div className={classes.resultDetailLabel}>
                    As Per Collateral Value
                  </div>

                  <div className={classes.resultDetailValue}>
                    {formatCurrency(collateralValue)} 
                  </div>
                </div>
                {scheme === "bullet" &&
                  <div className={classes.resultDetail}>
                    <div className={classes.resultDetailLabel}>Future Interest</div>

                    <div className={classes.resultDetailValue}>
                      {formatCurrency(futureInterest)}
                    </div>
                  </div>
                } 
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        alignItems="center"
        className={classes.footer}
      >
        {/* <Grid item xs={12} md={8}>
          <div className={classes.note}>
            <strong>ⓘ &nbsp; Note:</strong>&nbsp; Loan eligibility is calculated
            based on the 22K equivalent weight and selected loan parameters.
            Actual eligibility may vary as per bank policies.
          </div>
        </Grid> */}

        <Grid item xs={12} md={12} style={{maxWidth:"30%", margin:"10px auto"}}>
          <Button
            variant="contained"
            className={classes.calculateButton}
            onClick={calculateEligibility}
          >
            Calculate
          </Button>
        </Grid>
      </Grid>
      {callInProgress && (
        <CircularProgresss callInProgress={callInProgress} />
      )}
      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}
    </div>
  );
};

CriffCalculator.propTypes = {};

export default CriffCalculator;